"use client";

import { useArchiveControl } from "@/hooks/useArchiveControl";
import { PropertyStatus } from "@/types/property-status";
import { Popover, PopoverContent, PopoverTrigger } from "@nextui-org/react";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "@/i18n/routing";
import Image from "next/image";
import React, { useState } from "react";
import { SimpleArchiveModal } from "@/ui/Modals/SimpleArchiveModal";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FeatureFlags } from "@/enums/feature-flags";
import { useUserRole } from "@/hooks/useUserRole";
import ArchiveModal from "@/ui/Modals/ArchiveModal";

interface Props {
  objectId: string;
  slug: string;
  title: string;
  status: string;
  userCanArchive?: boolean;
}

interface IAction {
  id: number;
  label: string;
  action: () => void;
}

const ObjectActionsMenu: React.FC<Props> = (props) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const router = useRouter();
  const t = useTranslations();
  const {
    canArchive,
    canRestore,
    isArchiveModalOpen,
    isRestoreModalOpen,
    openArchiveModal,
    openRestoreModal,
    closeArchiveModal,
    closeRestoreModal,
    handleArchive,
    handleRestore,
    isArchiving,
    isRestoring,
  } = useArchiveControl({
    propertyId: props.objectId,
    propertyTitle: props.title,
    userCanArchive: props.userCanArchive,
  });
  const isPropertyArchived = props.status === PropertyStatus.ARCHIVED;
  const isArchived =
    canArchive && !isPropertyArchived && props.status !== "archived";
  const isRestored =
    canRestore && isPropertyArchived && props.status === "archived";
  const locale = useLocale();
  const { isAdmin, isSuperAdmin, isRealtor } = useUserRole();
  const hasAccess = isAdmin || isSuperAdmin || isRealtor;

  const propertyArchiveActionEnabled = useFeatureFlag(
    FeatureFlags.PROPERTY_ARCHIVE_ACTION,
  );

  const generateActions = () => {
    const actions: IAction[] = [
      {
        id: 1,
        label: t("Edit"),
        action: () => {
          router.push(`/profile/edit-object/${props.slug}`, { locale });
          setIsOpen(false);
        },
      },
    ];

    if (!propertyArchiveActionEnabled) return actions;

    if (isArchived || (props.userCanArchive && props.status !== "archived")) {
      actions.push({
        id: 2,
        label: t("Archive"),
        action: () => {
          openArchiveModal();
          setIsOpen(false);
        },
      });
    } else if (isRestored) {
      actions.push({
        id: 2,
        label: t("Restore"),
        action: () => {
          openRestoreModal();
          setIsOpen(false);
        },
      });
    }

    return actions;
  };

  return (
    <>
      <Popover
        isOpen={isOpen}
        onOpenChange={(value) => setIsOpen(value)}
        placement="bottom-start"
        classNames={{
          content: "min-w-[200px] p-0",
          trigger: "aria-expanded:opacity-[1]",
        }}
      >
        <PopoverTrigger>
          <button>
            <Image
              src={
                isOpen
                  ? "/assets/icons/ui/actions/close.svg"
                  : "/assets/icons/ui/actions/menu.svg"
              }
              width={30}
              height={30}
              alt="menu button"
            />
          </button>
        </PopoverTrigger>
        <PopoverContent>
          <ul className="flex flex-col w-full">
            {generateActions().map((item) => (
              <li
                key={item.id}
                className="border-b border-b-[#D7D7D759] last:border-b-0 w-full"
              >
                <button
                  onClick={item.action}
                  className="flex justify-start pl-6 pr-6 pt-5 pb-5"
                >
                  <span className="text-[#323232] text-2xl not-italic leading-[100%] tracking-normal align-middle">
                    {item.label}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>

      <SimpleArchiveModal
        isOpen={isArchiveModalOpen}
        onClose={closeArchiveModal}
        onArchive={handleArchive}
        mode="archive"
        propertyTitle={props.title}
        isLoading={isArchiving}
      />
      <SimpleArchiveModal
        isOpen={isRestoreModalOpen}
        onClose={closeRestoreModal}
        onArchive={handleRestore}
        mode="restore"
        propertyTitle={props.title}
        isLoading={isRestoring}
      />
    </>
  );
};

export default ObjectActionsMenu;
